/**
 * ClientTabBar Component
 *
 * Adobe-style folder tabs sit on top of the panel below them.
 * Inactive tabs are subtle (no chrome, just text + icon, with hairline
 * separators between them).
 * The active tab "drops" down with a spring animation, picks up white card
 * chrome with rounded top corners, and merges into the panel underneath.
 */

import { motion } from "motion/react";
import { useState } from "react";
import {
  ChartPieSlice,
  Files,
  Plugs,
  ShareNetwork,
  CalendarBlank,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";

/**
 * Canonical tab ids — exported for the `?tab=` search-param validation on the
 * $clientId route (Story 32.1 AC #15). Ids and labels are UNCHANGED; this
 * narrows ClientTabId from `string` to the literal union so deep links are
 * type-checked end to end.
 */
export const CLIENT_TAB_IDS = [
  "dashboards",
  "reports",
  "schedules",
  "integrations",
  "client-portal",
] as const;

export type ClientTabId = (typeof CLIENT_TAB_IDS)[number];

/**
 * Type guard for the `?tab=` search param (Story 32.1 AC #15). Lives here —
 * next to the id list — so the route's validateSearch can't drift from the
 * rendered tabs. Garbage values fail the guard and fall back to the default
 * tab (no crash).
 */
export function isClientTabId(value: unknown): value is ClientTabId {
  return (
    typeof value === "string" && (CLIENT_TAB_IDS as readonly string[]).includes(value)
  );
}

const CLIENT_TABS: ReadonlyArray<{ id: ClientTabId; label: string; icon: Icon }> = [
  { id: "dashboards", label: "Dashboards", icon: ChartPieSlice },
  { id: "reports", label: "Reports", icon: Files },
  { id: "schedules", label: "Schedules", icon: CalendarBlank },
  { id: "integrations", label: "Integrations", icon: Plugs },
  { id: "client-portal", label: "Client Portal", icon: ShareNetwork },
];

interface ClientTabBarProps {
  activeTab: ClientTabId;
  onTabChange: (tabId: ClientTabId) => void;
}

const ACTIVE_BORDER = "hsla(214, 25%, 70%, 1)";
const INACTIVE_BORDER = "hsl(var(--input))";

export function ClientTabBar({ activeTab, onTabChange }: ClientTabBarProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  return (
    <div
      className="relative flex items-end justify-center gap-1"
      role="tablist"
      aria-label="Client sections"
    >
      {CLIENT_TABS.map((tab, idx) => {
        const isActive = activeTab === tab.id;
        const isHovered = hoveredId === tab.id;
        const sideBorder = isActive || (!isActive && isHovered) ? ACTIVE_BORDER : INACTIVE_BORDER;
        const bottomBorder = isActive ? "hsla(0, 0%, 100%, 1)" : sideBorder;
        const TabIcon = tab.icon;
        return (
          <div key={tab.id} className="flex items-center">
            {idx > 0 && <span aria-hidden className="mx-1 h-5 w-px" />}
            <motion.button
              role="tab"
              aria-selected={isActive}
              onClick={() => onTabChange(tab.id)}
              onMouseEnter={() => !isActive && setHoveredId(tab.id)}
              onMouseLeave={() => setHoveredId((curr) => (curr === tab.id ? null : curr))}
              initial={false}
              animate={{
                y: isActive ? 12 : -8,
                borderBottomLeftRadius: isActive ? 0 : 16,
                borderBottomRightRadius: isActive ? 0 : 16,
                backgroundColor: isActive ? "hsla(0, 0%, 100%, 1)" : "hsla(0, 0%, 100%, 0.75)",
                clipPath: isActive ? "inset(-4px -4px 4px -4px)" : "inset(-20px)",
              }}
              transition={{ duration: 0.01, ease: "easeInOut" }}
              className={`pointer-events-auto relative -mb-px inline-flex items-center gap-2 rounded-t-xl px-5 pb-3 pt-3 font-medium cursor-pointer outline-none focus:outline-none focus-visible:outline-none transition-all ${
                isActive
                  ? "text-[14px] text-foreground"
                  : "text-[13.5px] text-foreground/70 shadow-xs hover:text-foreground hover:-translate-y-0.5 hover:shadow-sm"
              }`}
              style={{
                borderTop: `0.5px solid ${sideBorder}`,
                borderLeft: `0.5px solid ${sideBorder}`,
                borderRight: `0.5px solid ${sideBorder}`,
                borderBottom: `0.5px solid ${bottomBorder}`,
              }}
            >
              <TabIcon size={19} className="shrink-0 mr-1.5" />
              {tab.label}
            </motion.button>
          </div>
        );
      })}
    </div>
  );
}
